import { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Sparkles, FolderKanban, ChevronRight, ShieldCheck, Menu, LogOut, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

function Brand({ onClick }) {
  return (
    <button
      data-testid="brand-home-btn"
      onClick={onClick}
      className="flex items-center gap-2.5 px-1 text-left"
    >
      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-foreground text-background">
        <Sparkles className="h-4.5 w-4.5" strokeWidth={2.2} />
      </div>
      <div className="leading-tight">
        <div className="text-[15px] font-extrabold tracking-tight">Verba</div>
        <div className="text-[11px] text-muted-foreground">Bilgi Asistanı Platformu</div>
      </div>
    </button>
  );
}

function SidebarInner({ projects, onNavigate }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const go = (path) => { navigate(path); onNavigate?.(); };
  const doLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  return (
    <div className="flex h-full flex-col">
      <div className="px-5 py-5">
        <Brand onClick={() => go("/")} />
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-3">
        <button
          data-testid="nav-projects-btn"
          onClick={() => go("/")}
          className={cn(
            "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
            location.pathname === "/" ? "bg-foreground text-background" : "text-foreground hover:bg-secondary"
          )}
        >
          <FolderKanban className="h-4 w-4" />
          Projeler
        </button>

        {projects.length > 0 && (
          <div className="pt-4">
            <div className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Projelerim
            </div>
            {projects.map((p) => (
              <button
                key={p.id}
                data-testid={`sidebar-project-${p.id}`}
                onClick={() => go(`/projects/${p.id}`)}
                className={cn(
                  "group flex w-full items-center justify-between rounded-lg px-3 py-1.5 text-sm transition-colors hover:bg-secondary",
                  location.pathname.includes(p.id) ? "text-foreground" : "text-muted-foreground"
                )}
              >
                <span className="truncate">{p.name}</span>
                <ChevronRight className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-60" />
              </button>
            ))}
          </div>
        )}
      </nav>

      <div className="space-y-2 border-t border-border p-4">
        <div className="flex items-center gap-2 rounded-lg bg-secondary/60 px-3 py-2 text-xs text-muted-foreground">
          <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600" />
          <span>Kaynaklı & denetlenebilir cevaplar</span>
        </div>
        <div className="flex items-center justify-between gap-2 rounded-lg px-1 py-1">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Yönetici</div>
            <div data-testid="current-user-email" className="truncate text-xs text-foreground">{user?.email}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={doLogout}
            title="Çıkış yap"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function SystemStatusBanner() {
  const [status, setStatus] = useState(null);
  useEffect(() => { api.systemStatus().then(setStatus).catch(() => {}); }, []);
  if (!status || status.qdrant?.ok) return null;
  return (
    <div
      data-testid="system-status-banner"
      className="mb-6 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <div>
        <div className="font-semibold">Vektör veritabanına (Qdrant) ulaşılamıyor</div>
        <div className="mt-0.5 text-xs text-red-700/90">
          Sohbet ve doküman işleme bu sırada çalışmaz. Kümenin askıya alınmış (suspended) olup olmadığını kontrol edin.
          {status.qdrant?.error ? <span className="mono"> — {status.qdrant.error}</span> : null}
        </div>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.getProjects().then(setProjects).catch(() => {});
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen bg-secondary/30 grain">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-20 hidden w-[264px] flex-col border-r border-border bg-background/80 backdrop-blur-xl md:flex">
        <SidebarInner projects={projects} />
      </aside>

      {/* Mobile top bar */}
      <header className="fixed inset-x-0 top-0 z-30 flex items-center gap-3 border-b border-border bg-background/85 px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] backdrop-blur-xl md:hidden">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="mobile-menu-btn"
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border hover:bg-secondary"
              aria-label="Menü"
            >
              <Menu className="h-5 w-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[280px] p-0" data-testid="mobile-sidebar">
            <SheetTitle className="sr-only">Menü</SheetTitle>
            <SidebarInner projects={projects} onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-foreground text-background">
            <Sparkles className="h-4 w-4" strokeWidth={2.2} />
          </div>
          <span className="text-[15px] font-extrabold tracking-tight">Verba</span>
        </div>
      </header>

      <main className="flex-1 pt-mobilebar md:pl-[264px] md:pt-0">
        <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-10 lg:px-10">
          <SystemStatusBanner />
          <Outlet />
        </div>
      </main>
    </div>
  );
}
